
import { PingAuthenticationService } from "@aa-techops-ui/ping-authentication";
import { computed, effect, inject, Injectable, Injector, resource, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { PingProfile, PingRole } from "./models/ping-profile.model";
import { ApiService } from "../api-service/api.service";

@Injectable()
export class UserService {
    private readonly ping = inject(PingAuthenticationService);
    private readonly api = inject(ApiService);
    private readonly injector = inject(Injector);
    private pilotStatusPromise: Promise<void> | null = null;

    private pingProfile = toSignal<PingProfile>(
        this.ping.profile$,
        { initialValue: null }
    );

    private pingRoles = toSignal<string[]>(
        this.ping.roles$,
        { initialValue: null }
    );

    employeeId = computed(() => {
        if (this.pingProfile()) {
            const id = this.pingProfile()?.uid;
            if (id) {
                return parseInt(id);
            }
        }
        return 0;
    });

    firstName = computed(() => this.pingProfile()?.first_name);
    lastName = computed(() => this.pingProfile()?.last_name);

    role = computed<PingRole>(() => {
        const roles = this.pingRoles();
        if (roles && roles.length > 0) {
            return roles.at(0) as PingRole;
        } else {
            return PingRole.Pilot;
        }
    });

    isAdminOrReviewer = computed(() => {
        const role = this.role();
        return role === PingRole.Admin || role === PingRole.Reviewer;
    });

    private pilotCheckEmployeeId = signal<number | undefined>(undefined);

    private medicalRecordResource = resource({
        params: () => this.pilotCheckEmployeeId(),
        loader: ({ params: employeeId }) => this.api.viewMedicalRecord(employeeId),
    });

    isPilot = computed(() => {
        if (this.isAdminOrReviewer()) return null;
        if (this.medicalRecordResource.error()) return false;
        if (!this.medicalRecordResource.hasValue()) return null;
        const crewData = this.medicalRecordResource.value().medicalRecord?.crewData;
        return !!crewData && crewData.crewType === 'P';
    });

    displayRole = computed(() => {
        const role = this.role();

        if (role === PingRole.Admin || role === PingRole.Reviewer) {
            return role;
        }

        if (this.isPilot()) {
            return PingRole.Pilot;
        }

        return 'N/A';
    });

    async checkPilotStatus(employeeId?: number): Promise<void> {
        if (this.isAdminOrReviewer()) return;
        if (this.isPilot() !== null) return;
        if (this.pilotStatusPromise) return this.pilotStatusPromise;

        const id = employeeId ?? this.employeeId();
        if (!id || id <= 0) return;

        this.pilotCheckEmployeeId.set(id);

        this.pilotStatusPromise = new Promise<void>(resolve => {
            const ref = effect(() => {
                if (this.isPilot() !== null) {
                    ref.destroy();
                    this.pilotStatusPromise = null;
                    resolve();
                }
            }, { injector: this.injector });
        });

        return this.pilotStatusPromise;
    }
}